<template>
  <div class="recommend-num-list">
    <!-- 彩票类型切换标签：切换后重新查询第一页，默认双色球 -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <!-- 推荐号码列表：每条记录一个白色卡片 -->
    <div class="rec-list">
      <!-- 空状态：该类型还没有推荐号码 -->
      <div v-if="!recList.length && !loading" class="rec-empty">暂无推荐号码</div>

      <div v-for="(item, index) in recList" :key="index" class="rec-card">
        <!-- 第一行：期号在左，投注时间在右 -->
        <div class="rec-head">
          <span class="rec-issue">第 {{ item.recommendBetNumIssue }} 期</span>
          <span v-if="item.recommendBetNumTime" class="rec-bettime">投注时间：{{ formatDrawTime(item.recommendBetNumTime) }}</span>
        </div>

        <!-- 第二行：玩法说明在左，模型徽标紧跟其后 -->
        <div class="rec-sub">
          <span class="rec-type">{{ playTypeText(item) }}</span>
          <span v-if="item.recommendModel" class="rec-model">{{ item.recommendModel }}</span>
        </div>

        <!-- 第三行：推荐号码球，redNum 红色 blueNum 蓝色 -->
        <div class="rec-nums">
          <span
            v-for="(n, i) in splitNums(item.betNumDTO && item.betNumDTO.redNum)"
            :key="'r' + i"
            class="num-ball"
          >{{ n }}</span>
          <template v-if="splitNums(item.betNumDTO && item.betNumDTO.blueNum).length">
            <span class="num-plus">+</span>
            <span
              v-for="(n, i) in splitNums(item.betNumDTO && item.betNumDTO.blueNum)"
              :key="'b' + i"
              class="num-ball blue"
            >{{ n }}</span>
          </template>
        </div>

        <!-- 兑奖结果：已兑奖(alreadyPrize)展示 prizeDetail 详情，中奖绿/未中灰 -->
        <div
          v-if="item.recommendNumStatus === 'alreadyPrize' && item.prizeDetail"
          class="rec-prize"
          :class="isPrizeWin(item.prizeDetail) ? 'win' : 'lose'"
        >
          {{ item.prizeDetail }}
        </div>

        <!-- 待兑奖：状态为 pending 时展示橙色提示条 -->
        <div v-else-if="item.recommendNumStatus === 'pending'" class="rec-pending">待兑奖</div>
      </div>

      <!-- 加载更多：还有下一页时显示 -->
      <button
        v-if="recList.length < recTotal"
        class="load-more"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? '加载中...' : '加载更多' }}
      </button>
    </div>
  </div>
</template>

<script>
// 导入模型推荐号码分页查询接口
import { pageRecommandNum } from '@/api/lottery'
// 彩票类型枚举统一从工具文件引入（与购买/兑奖页共用一份）
import { lotteryTypeList, plsModeDescMap } from '@/utils/lottery'

export default {
  name: 'RecommendBetNumListView',
  data() {
    return {
      // 彩票类型枚举，循环渲染成顶部类型标签栏
      typeList: lotteryTypeList,
      // 当前查询的彩票类型，默认双色球（请求参数名 lotteryType）
      lotteryType: 'ssq',
      // 推荐号码列表数据
      recList: [],
      // 总条数（用于判断是否还有下一页，显示"加载更多"）
      recTotal: 0,
      // 当前页码，从 1 开始
      pageNum: 1,
      // 每页条数
      pageSize: 10,
      // 加载中标志：防止重复请求
      loading: false
    }
  },
  // 组件创建后请求第一页数据
  created() {
    this.fetchRecList()
  },
  methods: {
    // 切换彩票类型：重置回第一页重新查询
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
      this.pageNum = 1
      this.recList = []
      this.fetchRecList()
    },

    // 加载更多：页码 +1 后追加到列表尾部
    loadMore() {
      this.pageNum += 1
      this.fetchRecList()
    },

    // 请求模型推荐号码列表：按当前类型 + 页码查询，第一页覆盖、后续页追加
    async fetchRecList() {
      if (this.loading) return
      this.loading = true
      try {
        const res = await pageRecommandNum({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          lotteryType: this.lotteryType
        })
        // 后端返回：{ code: "200", data: { total, rows }, msg, success }
        if (res.code === '200' || res.success) {
          this.recTotal = (res.data && res.data.total) || 0
          const rows = (res.data && res.data.rows) || []
          // 第 1 页覆盖列表，第 2 页往后追加到尾部
          this.recList = this.pageNum === 1 ? rows : this.recList.concat(rows)
        } else {
          alert(res.msg || '查询推荐号码失败，请稍后重试')
        }
      } catch (err) {
        console.error('查询推荐号码失败：', err)
        alert('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 玩法展示文字：按类型把 playType 编码翻译成中文
    playTypeText(item) {
      const mode = item.playType
      const type = item.lotteryType
      // 快乐8、七乐彩：playType 就是选号个数，如 9 -> 选 9
      if (type === 'klb' || type === 'qlc') return `选 ${mode}`
      // 排列三/排列五：说明取自枚举表
      if (type === 'pls' || type === 'plw') return plsModeDescMap[mode] || mode
      // 双色球/大乐透：6+1 -> 6 红 + 1 蓝
      if (mode && mode.indexOf('+') > -1) {
        const parts = mode.split('+')
        if (type === 'ssq') return `${parts[0]} 红 + ${parts[1]} 蓝`
        if (type === 'dlt') return `${parts[0]} 前 + ${parts[1]} 后`
      }
      return mode || ''
    },

    // 把英文逗号拼接的号码字符串拆成数组，空值返回空数组
    splitNums(numStr) {
      return (numStr || '').split(',').filter(Boolean)
    },

    // 开奖时间格式化："2026-10-15 21:30:00" 精简成 "10-15 21:30"
    formatDrawTime(time) {
      if (!time) return ''
      const parts = String(time).split(' ')
      const date = parts[0] ? parts[0].slice(5) : ''    // 2026-10-15 -> 10-15
      const hm = parts[1] ? parts[1].slice(0, 5) : ''   // 21:30:00 -> 21:30
      return (date || '') + ' ' + hm
    },

    // 判断兑奖结果是否中奖：文字包含"中奖"且不包含"未中奖"视为中奖
    isPrizeWin(text) {
      const txt = text || ''
      return txt.indexOf('中奖') > -1 && txt.indexOf('未中奖') === -1
    }
  }
}
</script>

<style scoped>
/* ===== 类型切换标签栏：与出号页/兑奖页一致，一屏最多 4 个，可横向滑动 ===== */
.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

/* ===== 推荐号码列表 ===== */
.rec-list {
  padding: 12px;
}

/* 单条推荐记录卡片 */
.rec-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

/* 第一行/第二行公共布局：左右两端对齐 */
.rec-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

/* 第一行左侧：期号，加粗醒目 */
.rec-issue {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
}

/* 第一行右侧：投注时间，灰色小字 */
.rec-bettime {
  font-size: 12px;
  color: #909399;
}

/* 第二行：玩法说明 + 模型徽标（左对齐，徽标紧跟玩法后面） */
.rec-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.rec-type {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

/* 推荐模型徽标：紫色圆角小标签 */
.rec-model {
  font-size: 11px;
  color: #667eea;
  background: #eef0fb;
  border-radius: 10px;
  padding: 2px 8px;
  white-space: nowrap;
}

/* 号码球区域：允许换行（快乐8选九有 9 个号码） */
.rec-nums {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}

/* 号码球样式与开奖号码列表页（DrawNumView）保持一致：实色圆球 */
.num-ball {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f56c6c; /* 主号码：红色球 */
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  box-sizing: border-box;
}

/* 辅助号码：蓝色球 */
.num-ball.blue {
  background: #409eff;
}

/* 主辅号码之间的加号 */
.num-plus {
  color: #909399;
  font-weight: bold;
}

/* 小屏手机：号码球略缩小，与开奖列表页适配一致 */
@media screen and (max-width: 360px) {
  .num-ball {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
}

/* 兑奖结果详情：已兑奖才展示，中奖绿/未中灰 */
.rec-prize {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  word-break: break-all;
}

.rec-prize.win {
  background: #f0f9eb;
  color: #67c23a;
}

.rec-prize.lose {
  background: #f4f4f5;
  color: #909399;
}

/* 待兑奖提示条：橙色底+橙字，样式与兑奖结果条一致 */
.rec-pending {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  background: #fdf6ec;
  color: #e6a23c;
  font-weight: bold;
}

/* 空状态提示 */
.rec-empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 40px 0;
}

/* 加载更多按钮：通栏白色描边按钮 */
.load-more {
  width: 100%;
  height: 38px;
  border: 1px solid #dcdfe6;
  border-radius: 19px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  cursor: pointer;
}

.load-more:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
